import { useQueryParams } from "hooks/useQueryParams";
import { useCallback, useState } from "react";

interface UsePageProps {
  isURLDriven: boolean;
}

/**
 * usePage manages the current page index for a datagrid.
 *
 * If `isURLDriven` is true, the page state is synced with the `page` query param.
 * Otherwise, the page is managed locally via internal state.
 *
 * Automatically resets the query param to default (removes `page`) when set to 0.
 */
export function usePage({ isURLDriven }: UsePageProps) {
  const { filters: queryParams, setFilters: setQueryParams } = useQueryParams<{
    page?: string;
  }>();

  const [internalPage, setInternalPage] = useState(0);

  // ast-grep-ignore: no-unsafe-number-parsing
  const page = isURLDriven ? Number(queryParams.page ?? 0) : internalPage;

  const setPage = useCallback(
    (value: number) => {
      if (isURLDriven) {
        setQueryParams((f) => {
          if (value === 0) {
            delete f.page;
          } else {
            f.page = String(value);
          }
        });
      } else {
        setInternalPage(value);
      }
    },
    [isURLDriven, setQueryParams]
  );

  return { page, setPage };
}
